import { isNil, get } from "lodash";

import {
  BooleanCell,
  EditableBooleanCell,
  EditableNumberCell,
  EditableSelectCell,
  EditableStringCell,
  IdCell,
  SelectCell,
  StringCell,
  DateTimeCell,
  EditableDateTimeCell,
} from "@/react/components/table/components/cells";
import {
  BooleanFilter,
  BooleanFilterDisplay,
  isEmptyFilterFn,
  isntEmptyFilterFn,
  ListFilter,
  NumberFilter,
  NumberRangeFilter,
  SelectFilter,
  SelectFilterDisplay,
  StringFilter,
  StringFilterDisplay,
} from "@/react/components/table/components/filter/filters";
import ColumnSetter from "@/react/components/table/components/ColumnSetter";
import {
  DurationCell,
  EditableDurationCell,
  DurationColumnSetter,
} from "@/react/components/table/components/duration";
import { formatTimestamp } from "@/react/datetime";

const STRING = "string";
const BOOLEAN = "boolean";
const NUMBER = "number";
const UNIX_TIMESTAMP = "unix_timestamp";
const SELECT = "select";
const ID = "id";
const DURATION = "duration";

// TODO: Set this letter width based on the actual font. Not sure how to do that.
// If the letter width is not right, then the longer the text, the more imprecise the width will be
// and that leads to the column either being too small for the text, or there being too much empty space
const COLUMN_WIDTH_PROPERTIES = {
  header: {
    letterWidth: 9,
    offset: 35,
    minWidth: 50,
    maxWidth: 1000,
  },
  cell: {
    letterWidth: 9,
    offset: 30,
    minWidth: 10,
    maxWidth: 2000,
  },
};

export const TYPES = Object.freeze({
  STRING,
  BOOLEAN,
  NUMBER,
  UNIX_TIMESTAMP,
  SELECT,
  ID,
  DURATION,
});

export const columnTypes = Object.freeze({
  [STRING]: {
    cell: StringCell,
    editableCell: EditableStringCell,
    sortingFn: "alphanumeric",
    filterFns: new Map([
      [
        "contains",
        {
          label: "Contains",
          filterFn: "includesString",
          filterComponent: StringFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      [
        "is",
        {
          label: "Is",
          filterFn: "equalsString",
          filterComponent: StringFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      isEmptyFilterFn,
      isntEmptyFilterFn,
    ]),
  },
  [BOOLEAN]: {
    cell: BooleanCell,
    editableCell: EditableBooleanCell,
    sortingFn: "basic",
    filterFns: new Map([
      [
        "isTrueOrFalsy",
        {
          label: "equals",
          filterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return filterValue === true ? filterValue === value : !value;
          },
          filterComponent: BooleanFilter,
          displayComponent: BooleanFilterDisplay,
        },
      ],
    ]),
    downloadFn: (value) => (value ? "Yes" : "No"),
    uploadFn: (value) => value === "Yes",
  },
  [NUMBER]: {
    cell: StringCell,
    editableCell: EditableNumberCell,
    sortingFn: "basic",
    filterFns: new Map([
      [
        "is",
        {
          label: "Is",
          filterFn: "equals",
          filterComponent: NumberFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      [
        "range",
        {
          label: "In range",
          filterFn: "inNumberRange",
          filterComponent: NumberRangeFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      isEmptyFilterFn,
      isntEmptyFilterFn,
    ]),
  },
  [SELECT]: {
    cell: SelectCell,
    editableCell: EditableSelectCell,
    sortingFn: "alphanumeric",
    filterFns: new Map([
      [
        "is",
        {
          label: "Is",
          filterFn: "equals",
          filterComponent: SelectFilter,
          displayComponent: SelectFilterDisplay,
        },
      ],
      isEmptyFilterFn,
      isntEmptyFilterFn,
    ]),
  },
  [ID]: {
    cell: IdCell,
    sortingFn: "basic",
    filterFns: new Map([
      [
        "contains",
        {
          label: "Contains",
          filterFn: "includesString",
          filterComponent: StringFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      [
        "is",
        {
          label: "Is",
          filterFn: "equalsString",
          filterComponent: StringFilter,
          displayComponent: StringFilterDisplay,
        },
      ],
      [
        "in list",
        {
          label: "In list",
          filterFn: "arrIncludesSome",
          filterComponent: ListFilter,
          displayComponent: (displayFilter) =>
            `${displayFilter.filterFnId} (${displayFilter.value?.length || 0})`,
        },
      ],
      isEmptyFilterFn,
      isntEmptyFilterFn,
    ]),
  },
  [DURATION]: {
    cell: DurationCell,
    editableCell: EditableDurationCell,
    sortingFn: "basic",
    filterFns: new Map([isEmptyFilterFn, isntEmptyFilterFn]),
    columnSetter: DurationColumnSetter,
  },
  [UNIX_TIMESTAMP]: {
    cell: DateTimeCell,
    editableCell: EditableDateTimeCell,
    sortingFn: "basic",
    filterFns: new Map([isEmptyFilterFn, isntEmptyFilterFn]),
  },
});

const calculateWidth = (value, properties) => {
  if (isNil(value) || isNil(properties)) {
    return 0;
  }
  const length =
    typeof value === "string" ? value.length : value.toString().length;
  const width = length * properties.letterWidth + properties.offset;
  return Math.min(Math.max(width, properties.minWidth), properties.maxWidth);
};

// Set the width of columns that have no preset width based on how many characters are in the header
export const calculateColumnWidth = (columnDefs) => {
  return columnDefs.map((columnDef) => {
    if (columnDef.size || [BOOLEAN, NUMBER].includes(columnDef.type)) {
      return columnDef;
    }
    if (!columnDef.header || !columnDef.header.length) {
      throw new Error(
        `Column "${columnDef.accessorKey}" has no header defined`
      );
    }
    const minSize = columnDef.minSize ?? 0;
    return {
      ...columnDef,
      size: Math.max(
        calculateWidth(columnDef.header, COLUMN_WIDTH_PROPERTIES.header),
        minSize
      ),
    };
  });
};

// Create a mapping of column identifiers to their largest width based on the data
// If a column already has a 'size' property, use that as the width
export const columnWidthMapping = (columnDefs, data) => {
  let columnWidths = {};
  columnDefs.forEach((columnDef) => {
    const columnId = columnDef.id;
    let largestWidth = !isNil(columnDef.size)
      ? columnDef.size
      : calculateWidth(columnDef.header, COLUMN_WIDTH_PROPERTIES.header);
    data.forEach((row) => {
      let columnValue = get(row, columnId);
      // Dates are in unix timestamp format, so format it into the visible string to determine the real length
      if (columnDef.type === TYPES.UNIX_TIMESTAMP) {
        columnValue = columnDef.timestampFormatter
          ? columnDef.timestampFormatter(columnValue)
          : formatTimestamp(columnValue);
      }
      largestWidth = Math.max(
        calculateWidth(columnValue, COLUMN_WIDTH_PROPERTIES.cell),
        largestWidth
      );
    });
    columnWidths[columnId] = largestWidth;
  });
  return columnWidths;
};

export const mapColumnTypes = (columnDef) => {
  const type = columnDef.type || STRING;
  const columnType = columnTypes[type];

  if (!columnType) {
    throw new Error(
      `Unknown column type ${type}. Please define it in columnTypes`
    );
  }

  columnDef.cell ??= columnType.cell;

  if (columnDef.editable !== false) {
    columnDef.editableCell ??= columnType.editableCell;
    columnDef.editableColumn ??= ({ getValue, column, table, onChange }) =>
      columnDef.editableCell({
        getValue,
        column,
        row: null,
        table,
        externalOnChange: onChange,
      });
    columnDef.columnSetter ??= columnType?.columnSetter ?? ColumnSetter;
  }

  columnDef.sortingFn ??= columnType.sortingFn;
  // Serializers for file-actions
  /**
   * Serializes the value to a string that can be downloaded to a file
   * @see exportTable
   */
  columnDef.downloadFn ??= columnType.downloadFn;
  /**
   * De-serializes the value from a string that was uploaded from a file
   * @see mapTableFlowImportedRows
   */
  columnDef.uploadFn ??= columnType.uploadFn;

  if (type === SELECT && !columnDef.options) {
    throw new Error(
      `Options map missing for column "${columnDef.header}" definition`
    );
  }

  if (columnDef.enableColumnFilter !== false) {
    columnDef.filterFns ??= columnType.filterFns;
  }

  return columnDef;
};

export const isColumnSetter = (cell) =>
  cell.row === null && cell.externalOnChange instanceof Function;
